import {
  HtmlSnapshotResponseState,
  OrderedHtmlSnapshotSegment,
} from '../types/aiChatbot';
import { ChatHtmlSnapshot, SignalREventNames } from '../types/signlaRTypes';
import { SendAIChatbotMessageRequestDTO } from './dtos/SendAIChatbotMessageRequestDTO';

export const SUPPORTED_HTML_SNAPSHOT_VERSION = 1;

export const AI_RESPONSE_END_MARKERS = [
  '<!--This is the end-->',
  '<br><br><br>',
] as const;

const isAsciiLetter = (value: string | undefined): boolean =>
  value !== undefined && /[a-z]/i.test(value);

const canStartHtmlTag = (html: string, index: number): boolean => {
  const next = html[index + 1];
  if (next === undefined) return true;
  if (isAsciiLetter(next) || next === '!' || next === '?') return true;
  if (next !== '/') return false;

  const tagNameStart = html[index + 2];
  return tagNameStart === undefined || isAsciiLetter(tagNameStart);
};

const hasTransportEscapedHtmlTag = (html: string): boolean =>
  /(?:&lt;|&#0*60;|&#x0*3c;|\\u003c)(?:\/?[a-z]|!|\?)/i.test(html);

const decodeTransportEscapedHtml = (html: string): string => {
  if (!hasTransportEscapedHtmlTag(html)) return html;

  return html
    .replace(/&lt;|&#0*60;|&#x0*3c;/gi, '<')
    .replace(/&gt;|&#0*62;|&#x0*3e;/gi, '>')
    .replace(/&quot;|&#0*34;|&#x0*22;/gi, '"')
    .replace(/&apos;|&#0*39;|&#x0*27;/gi, "'")
    .replace(/&amp;|&#0*38;|&#x0*26;/gi, '&')
    .replace(/\\u003c/gi, '<')
    .replace(/\\u003e/gi, '>')
    .replace(/\\u0022/gi, '"')
    .replace(/\\u0027/gi, "'")
    .replace(/\\u0026/gi, '&');
};

/**
 * Decodes one transport-escaped HTML layer, then removes only a trailing,
 * lexically incomplete tag from renderable streaming content. Ordinary text
 * such as "value < 5" remains unchanged. Snapshot state retains the original
 * canonical payload.
 */
export const getRenderableStreamingHtml = (html: string): string => {
  const renderableHtml = decodeTransportEscapedHtml(html);
  let tagStart = -1;
  let quote: '"' | "'" | null = null;
  let isComment = false;

  for (let index = 0; index < renderableHtml.length; index += 1) {
    if (tagStart === -1) {
      if (
        renderableHtml[index] !== '<' ||
        !canStartHtmlTag(renderableHtml, index)
      ) {
        continue;
      }

      tagStart = index;
      isComment = renderableHtml.startsWith('<!--', index);
      if (isComment) index += 3;
      continue;
    }

    if (isComment) {
      if (renderableHtml.startsWith('-->', index)) {
        tagStart = -1;
        isComment = false;
        index += 2;
      }
      continue;
    }

    const character = renderableHtml[index];
    if (quote !== null) {
      if (character === quote) quote = null;
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
      continue;
    }
    if (character === '>') tagStart = -1;
  }

  return tagStart === -1
    ? renderableHtml
    : renderableHtml.slice(0, tagStart);
};

const getPendingLinkStart = (html: string): number | null => {
  const openLinkStarts: number[] = [];
  let linkTagStart = -1;
  let quote: '"' | "'" | null = null;
  let isClosingTag = false;

  for (let index = 0; index < html.length; index += 1) {
    if (linkTagStart === -1) {
      if (html[index] === '<' && /^<\/?a(?:\s|>|$)/i.test(html.slice(index))) {
        linkTagStart = index;
        isClosingTag = html[index + 1] === '/';
      }
      continue;
    }

    const character = html[index];
    if (quote !== null) {
      if (character === quote) quote = null;
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
      continue;
    }
    if (character !== '>') continue;

    const linkTag = html.slice(linkTagStart, index + 1);
    if (isClosingTag) {
      openLinkStarts.pop();
    } else if (!/\/\s*>$/.test(linkTag)) {
      openLinkStarts.push(linkTagStart);
    }
    linkTagStart = -1;
    isClosingTag = false;
  }

  if (linkTagStart !== -1) {
    return isClosingTag && openLinkStarts.length > 0
      ? openLinkStarts[openLinkStarts.length - 1]
      : linkTagStart;
  }

  return openLinkStarts.length > 0
    ? openLinkStarts[openLinkStarts.length - 1]
    : null;
};

export interface LinkAwareStreamingHtml {
  html: string;
  isPendingLink: boolean;
}

/**
 * During streaming, withholds an entire incomplete anchor so clients can keep
 * one stable pending-link indicator until the closing tag arrives.
 */
export const getLinkAwareStreamingHtml = (
  html: string,
  showPendingLink: boolean
): LinkAwareStreamingHtml => {
  if (!showPendingLink) {
    return {
      html: getRenderableStreamingHtml(html),
      isPendingLink: false,
    };
  }

  const normalizedHtml = decodeTransportEscapedHtml(html);
  const pendingLinkStart = getPendingLinkStart(normalizedHtml);
  return pendingLinkStart === null
    ? {
        html: getRenderableStreamingHtml(html),
        isPendingLink: false,
      }
    : {
        html: getRenderableStreamingHtml(
          normalizedHtml.slice(0, pendingLinkStart)
        ),
        isPendingLink: true,
      };
};

type HtmlSnapshotWirePayload = Record<string, unknown>;

const readSnapshotField = (
  payload: HtmlSnapshotWirePayload,
  camelCaseName: string,
  pascalCaseName: string
): unknown => {
  const camelCaseValue = payload[camelCaseName];
  const pascalCaseValue = payload[pascalCaseName];

  if (
    camelCaseValue !== undefined &&
    pascalCaseValue !== undefined &&
    camelCaseValue !== pascalCaseValue
  ) {
    throw new Error(
      `Conflicting chat HTML snapshot field values: ${camelCaseName}.`
    );
  }

  return camelCaseValue ?? pascalCaseValue;
};

export const normalizeChatHtmlSnapshot = (
  rawSnapshot: unknown
): ChatHtmlSnapshot => {
  if (
    !rawSnapshot ||
    typeof rawSnapshot !== 'object' ||
    Array.isArray(rawSnapshot)
  ) {
    throw new Error('Chat HTML snapshot payload must be an object.');
  }

  const payload = rawSnapshot as HtmlSnapshotWirePayload;
  const snapshot: ChatHtmlSnapshot = {
    version: readSnapshotField(payload, 'version', 'Version') as number,
    responseId: readSnapshotField(
      payload,
      'responseId',
      'ResponseId'
    ) as string,
    sessionId: readSnapshotField(
      payload,
      'sessionId',
      'SessionId'
    ) as string,
    agentId: readSnapshotField(
      payload,
      'agentId',
      'AgentId'
    ) as string | null,
    legacyTarget: readSnapshotField(
      payload,
      'legacyTarget',
      'LegacyTarget'
    ) as string,
    segmentId: readSnapshotField(
      payload,
      'segmentId',
      'SegmentId'
    ) as number,
    sequence: readSnapshotField(payload, 'sequence', 'Sequence') as number,
    html: readSnapshotField(payload, 'html', 'Html') as string,
    isSegmentFinal: readSnapshotField(
      payload,
      'isSegmentFinal',
      'IsSegmentFinal'
    ) as boolean,
  };

  if (!Number.isInteger(snapshot.version)) {
    throw new Error('Chat HTML snapshot version must be an integer.');
  }
  if (typeof snapshot.responseId !== 'string' || !snapshot.responseId.trim()) {
    throw new Error('Chat HTML snapshot responseId must be a non-empty string.');
  }
  if (typeof snapshot.sessionId !== 'string' || !snapshot.sessionId.trim()) {
    throw new Error('Chat HTML snapshot sessionId must be a non-empty string.');
  }
  if (snapshot.agentId !== null && typeof snapshot.agentId !== 'string') {
    throw new Error('Chat HTML snapshot agentId must be a string or null.');
  }
  if (
    typeof snapshot.legacyTarget !== 'string' ||
    !snapshot.legacyTarget.trim()
  ) {
    throw new Error(
      'Chat HTML snapshot legacyTarget must be a non-empty string.'
    );
  }
  if (!Number.isSafeInteger(snapshot.segmentId) || snapshot.segmentId < 1) {
    throw new Error('Chat HTML snapshot segmentId must be a positive safe integer.');
  }
  if (!Number.isSafeInteger(snapshot.sequence) || snapshot.sequence < 1) {
    throw new Error('Chat HTML snapshot sequence must be a positive safe integer.');
  }
  if (typeof snapshot.html !== 'string') {
    throw new Error('Chat HTML snapshot html must be a string.');
  }
  if (typeof snapshot.isSegmentFinal !== 'boolean') {
    throw new Error('Chat HTML snapshot isSegmentFinal must be a boolean.');
  }

  return snapshot;
};

export const isAIResponseEndMarker = (message: unknown): message is string =>
  typeof message === 'string' &&
  AI_RESPONSE_END_MARKERS.some((marker) => marker === message);

export const getLegacyTargetsForChatbotRoute = (
  chatbotRoute: SendAIChatbotMessageRequestDTO['chatbotRoute']
): string[] => {
  switch (chatbotRoute) {
    case 'mcpsylvia':
      return [
        SignalREventNames.NewAIChatMessageSylvia,
        SignalREventNames.NewAIMessageSylvia,
      ];
    case 'mcppadre':
      return [SignalREventNames.NewAIChatMessagePadre];
    case 'mcpmaya':
    default:
      return [
        SignalREventNames.NewAIChatMessageMaya,
        SignalREventNames.NewAIChatMessage,
      ];
  }
};

const getAgentIdForLegacyTarget = (legacyTarget: string): string => {
  switch (legacyTarget) {
    case SignalREventNames.NewAIChatMessageSylvia:
    case SignalREventNames.NewAIMessageSylvia:
      return 'sylvia';
    case SignalREventNames.NewAIChatMessagePadre:
      return 'padre';
    case SignalREventNames.NewAIChatMessage:
    case SignalREventNames.NewAIChatMessageMaya:
    default:
      return 'maya';
  }
};

export const createHtmlSnapshotResponse = (
  responseId: string,
  legacyTarget: string,
  sessionId = '',
  agentId: string | null = getAgentIdForLegacyTarget(legacyTarget)
): HtmlSnapshotResponseState => ({
  responseId,
  sessionId,
  agentId,
  legacyTarget,
  lastSequence: 0,
  segments: {},
  legacyExtras: [],
  isComplete: false,
});

export const applyHtmlSnapshotToResponse = (
  current: HtmlSnapshotResponseState | undefined,
  snapshot: ChatHtmlSnapshot
): HtmlSnapshotResponseState => {
  if (snapshot.version !== SUPPORTED_HTML_SNAPSHOT_VERSION) {
    throw new Error(
      `Unsupported chat HTML snapshot version: ${snapshot.version}.`
    );
  }

  const state =
    current ??
    createHtmlSnapshotResponse(
      snapshot.responseId,
      snapshot.legacyTarget,
      snapshot.sessionId,
      snapshot.agentId
    );

  if (state.responseId !== snapshot.responseId) {
    throw new Error('Snapshot response ID does not match its state key.');
  }

  if (state.sessionId && state.sessionId !== snapshot.sessionId) {
    throw new Error(
      `Snapshot session ID changed for response ${snapshot.responseId}.`
    );
  }

  if (state.agentId && state.agentId !== snapshot.agentId) {
    throw new Error(
      `Snapshot agent ID changed for response ${snapshot.responseId}.`
    );
  }

  const currentSegment = state.segments[snapshot.segmentId];
  if (currentSegment) {
    if (snapshot.sequence < currentSegment.sequence) return state;

    if (snapshot.sequence === currentSegment.sequence) {
      if (
        snapshot.html === currentSegment.html &&
        snapshot.isSegmentFinal === currentSegment.isFinal
      ) {
        return state;
      }
      throw new Error(
        `Conflicting chat HTML snapshot identity for response ${snapshot.responseId}, segment ${snapshot.segmentId}, sequence ${snapshot.sequence}.`
      );
    }

    if (currentSegment.isFinal) {
      throw new Error(
        `Chat HTML snapshot updated finalized segment ${snapshot.segmentId} for response ${snapshot.responseId}.`
      );
    }
  }

  return {
    ...state,
    sessionId: snapshot.sessionId,
    agentId: snapshot.agentId,
    legacyTarget: snapshot.legacyTarget,
    lastSequence: Math.max(state.lastSequence, snapshot.sequence),
    segments: {
      ...state.segments,
      [snapshot.segmentId]: {
        // Every event carries the complete canonical HTML for this segment.
        // Replacement is required; snapshot.html must never be appended.
        html: snapshot.html,
        sequence: snapshot.sequence,
        isFinal: snapshot.isSegmentFinal,
      },
    },
  };
};

export const getOrderedHtmlSnapshotSegments = (
  response: HtmlSnapshotResponseState | undefined
): OrderedHtmlSnapshotSegment[] =>
  Object.entries(response?.segments ?? {})
    .map(([segmentId, segment]) => ({
      segmentId: Number(segmentId),
      ...segment,
    }))
    .sort((left, right) => left.segmentId - right.segmentId);
